import { ReactElement, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Link, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { OnboardingGoal, UserOnboarding } from '../../api/userOnboarding';
import { useUpdateUserOnboardingMutation } from '../../hooks/queries/onboarding/useUserOnboarding';
import { Routes } from '../../enums/Routes';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';
import { UserRoles } from '../../api/queries';
import { checklistLinks, FirstStepResult, firstStepPath, isAssessable, isOpen, STATUS_CARDS } from './gettingStartedView';
import classes from './GettingStartedPanel.module.css';

const K = 'gettingStarted.home';

/** A tip in a dark bubble, under what it is about. «Lukk tipsene» closes both, until shown again. */
const Tip = ({ number, text }: { number: 1 | 2; text: string }): ReactElement => {
  const { t } = useTranslation();
  const save = useUpdateUserOnboardingMutation();
  return (
    <div role="note" aria-label={t(`${K}.tipLabel`)} className={classes.tip}>
      <Paragraph data-size="sm">{text}</Paragraph>
      <div className={classes.tipFooter}>
        <span>{t(`${K}.tipCount`, { number })}</span>
        <button type="button" className={classes.tipClose} onClick={() => save.mutate({ tipsClosed: true })}>
          {t(`${K}.closeTips`)}
        </button>
      </div>
    </div>
  );
};

/** What will appear here, or an example of it */
const StatusCards = ({ goal, example }: { goal: OnboardingGoal; example: boolean }): ReactElement => {
  const { t } = useTranslation();
  const mode = example ? 'example' : 'empty';
  return (
    <section aria-labelledby="getting-started-status" className={classes.status}>
      <Heading level={2} data-size="xs" id="getting-started-status" className="ds-sr-only">
        {t(`${K}.statusTitle`)}
      </Heading>
      <ul className={classes.cards}>
        {(STATUS_CARDS[goal] ?? []).map((card) => (
          <li key={card} className={classes.card} data-example={example || undefined}>
            <div className={classes.cardHead}>
              <span className="ds-label" data-size="sm">
                {t(`${K}.cards.${goal}.${card}.title`)}
              </span>
              <Tag data-color={example ? 'info' : 'neutral'} data-size="sm">
                {t(`${K}.cards.${goal}.${card}.${mode}.tag`)}
              </Tag>
            </div>
            <Paragraph className={classes.cardValue}>{t(`${K}.cards.${goal}.${card}.${mode}.value`)}</Paragraph>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t(`${K}.cards.${goal}.${card}.${mode}.text`)}
            </Paragraph>
          </li>
        ))}
      </ul>
    </section>
  );
};

/**
 * Before the mapping, the risk matrix decides what the employees see and when the risk is calculated. It is not a
 * must: the survey can be made first, and uses the standard settings.
 */
const RiskMatrixStep = ({ onboarding, goal }: { onboarding: UserOnboarding; goal: OnboardingGoal }): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <section aria-labelledby="getting-started-first" className={classes.first}>
        <span className={classes.eyebrow}>{t(`${K}.firstStep`)}</span>
        <Heading level={2} data-size="md" id="getting-started-first">
          {t(`${K}.first.riskMatrix.title`)}
        </Heading>
        <Paragraph>{t(`${K}.first.riskMatrix.text`)}</Paragraph>
        <ul className={classes.chips}>
          <li>{t(`${K}.first.riskMatrix.time`)}</li>
          <li>{t(`${K}.first.riskMatrix.nothingSent`)}</li>
        </ul>
        <div className={classes.actions}>
          <Button asChild>
            <RouterLink to={Routes.HRRiskMatrixFullPath}>{t(`${K}.first.riskMatrix.start`)}</RouterLink>
          </Button>
          <Button asChild variant="secondary">
            <RouterLink to={firstStepPath(goal)}>{t(`${K}.first.riskMatrix.skip`)}</RouterLink>
          </Button>
        </div>
        {!onboarding.tipsClosed && <Tip number={1} text={t(`${K}.tip1`)} />}
      </section>
      <StatusCards goal={goal} example={false} />
    </>
  );
};

/** The first step for the goal, before anything is made. Nothing is sent to anyone from here. */
const FirstStep = ({ onboarding, goal }: { onboarding: UserOnboarding; goal: OnboardingGoal }): ReactElement => {
  const { t } = useTranslation();
  const [example, setExample] = useState(false);
  if (isOpen(onboarding, 'riskMatrix')) return <RiskMatrixStep onboarding={onboarding} goal={goal} />;
  return (
    <>
      <section aria-labelledby="getting-started-first" className={classes.first}>
        <span className={classes.eyebrow}>{t(`${K}.firstStep`)}</span>
        <Heading level={2} data-size="md" id="getting-started-first">
          {t(`${K}.first.${goal}.title`)}
        </Heading>
        <Paragraph>{t(`${K}.first.${goal}.text`)}</Paragraph>
        <ul className={classes.chips}>
          <li>{t(`${K}.first.${goal}.time`)}</li>
          <li>{t(`${K}.nothingSent`)}</li>
        </ul>
        <div className={classes.actions}>
          <Button asChild>
            <RouterLink to={firstStepPath(goal)}>{t(`${K}.first.${goal}.start`)}</RouterLink>
          </Button>
          <Button variant="secondary" aria-pressed={example} onClick={() => setExample((shown) => !shown)}>
            {example ? t(`${K}.hideExample`) : t(`${K}.first.${goal}.example`)}
          </Button>
        </div>
        {!onboarding.tipsClosed && <Tip number={1} text={t(`${K}.tip1`)} />}
      </section>
      {example && (
        <Alert data-color="info" data-size="sm" className={classes.exampleBanner}>
          <Paragraph data-size="sm">{t(`${K}.exampleBanner`)}</Paragraph>
          <Button variant="tertiary" data-size="sm" onClick={() => setExample(false)}>
            {t(`${K}.hideExample`)}
          </Button>
        </Alert>
      )}
      <StatusCards goal={goal} example={example} />
    </>
  );
};

/** What comes next, when the user is ready. It never sends anything by itself. */
const NextStep = ({ onboarding, goal }: { onboarding: UserOnboarding; goal: OnboardingGoal }): ReactElement | null => {
  const { t } = useTranslation();
  const { data: userInfo } = useGetUserinfo();
  // Only an administrator can invite. Others add an existing user on HR's page for safety representatives.
  const mayInvite = Boolean(userInfo?.userRoles?.includes(UserRoles.ORG_ADMIN));
  const assessSurvey = onboarding.workEnvironment?.assessSurvey ?? null;
  // The risk can only be assessed once it is calculated, so the step waits while the team answers
  const next = onboarding.checklist.find((item) => !item.done && (item.key !== 'assessRisk' || isAssessable(assessSurvey)));
  if (!next) return null;
  // Too few answered: what to do instead is decided with the safety representative
  const text = next.key === 'assessRisk' && assessSurvey?.state === 'insufficient' ? 'assessRisk.insufficient' : next.key;
  const link = checklistLinks(onboarding, mayInvite)[next.key];
  return (
    <section aria-labelledby="getting-started-next" className={classes.next}>
      <span className={classes.eyebrow}>{t(`${K}.nextEyebrow`)}</span>
      <Heading level={2} data-size="sm" id="getting-started-next">
        {t(`${K}.next.${next.key}.title`)}
      </Heading>
      <Paragraph data-size="sm">{t(`${K}.next.${text}.text`)}</Paragraph>
      {link && (
        <Link asChild>
          <RouterLink to={link}>{t(`${K}.next.${text}.link`)}</RouterLink>
        </Link>
      )}
      {!onboarding.tipsClosed && <Tip number={2} text={t(`${K}.tip2.${goal}`)} />}
    </section>
  );
};

/**
 * «Kom i gang» on the page the goal is worked on later: the first step and what will appear here,
 * or, once it is done, the step after it.
 */
export const GettingStartedPanel = ({ onboarding }: { onboarding: UserOnboarding }): ReactElement | null => {
  const { t } = useTranslation();
  const location = useLocation();
  // Back from the first step: what was made, and that nobody has it yet when it is planned
  const made = (location.state as { firstStep?: FirstStepResult } | null)?.firstStep;
  const goal = onboarding.goal;
  if (!goal || !onboarding.gettingStarted) return null;
  return (
    <>
      {made && onboarding.firstStepDone && (
        <Alert data-color="success" role="status">
          <Paragraph>
            {t(`gettingStarted.banner.done.${made.planned ? 'planned' : 'made'}`, { title: made.title, team: made.team })}
          </Paragraph>
        </Alert>
      )}
      {onboarding.firstStepDone ? (
        <NextStep onboarding={onboarding} goal={goal} />
      ) : (
        <FirstStep onboarding={onboarding} goal={goal} />
      )}
    </>
  );
};
